import type {
  BaseEntityRow,
  CanvasMetaRow,
  ConversationRow,
  EntityMetaRow,
  CollectionRow,
  SnapshotInfo,
  SpaceMetaRow,
} from './entity-store';

// ---------------------------------------------------------------------------
// Runtime validators for server-sent WS entity-event payloads
//
// These guards protect the hot path in entityEventHandler / entityStore from
// silently applying garbage when the server changes a field name or type.
// On failure the caller must log + drop the message rather than apply it.
//
// Design notes:
// - Hand-rolled typeof checks — no zod dependency (not in project).
// - Extra fields are tolerated (production servers regularly add fields).
// - Guards are O(1) per call; cost is negligible vs. the network round-trip.
//
// M9.5-Unify Task 10: extracted `isBaseEntityRow` so per-kind validators
// share one definition of the unified shape and only narrow on `kind` and
// kind-specific fields.
// ---------------------------------------------------------------------------

function isString(x: unknown): x is string {
  return typeof x === 'string';
}

function isNullableString(x: unknown): x is string | null {
  return x === null || typeof x === 'string';
}

function isBoolean(x: unknown): x is boolean {
  return typeof x === 'boolean';
}

function isStringArray(x: unknown): x is string[] {
  return Array.isArray(x);
}

function isObject(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

const SUPPORTED_BASE_KINDS: ReadonlySet<string> = new Set([
  'document',
  'collection',
  'canvas',
  'space',
  'conversation',
]);

// ---------------------------------------------------------------------------
// BaseEntityRow — the universal entity wire shape (M9.5-Unify)
// ---------------------------------------------------------------------------

function isAccess(x: unknown): boolean {
  if (!isObject(x)) return false;
  if (!isString(x.permission)) return false;
  if (!isString(x.grantedAt)) return false;
  // grantedBy may be null or an object — skip deep validation
  if (!isString(x.via)) return false;
  return true;
}

export function isBaseEntityRow(x: unknown): x is BaseEntityRow {
  if (!isObject(x)) return false;
  if (!isString(x.id)) return false;
  if (!isString(x.kind) || !SUPPORTED_BASE_KINDS.has(x.kind)) return false;
  if (!isString(x.title)) return false;
  if (!isString(x.snippet)) return false;
  if (!isBoolean(x.isArchived)) return false;
  if (!isStringArray(x.tags)) return false;
  if (!Array.isArray(x.collectionIds)) return false;
  if (!isNullableString(x.previewMarkup)) return false;
  if (!isNullableString(x.previewKind)) return false;
  // version is optional: conversations omit it (M1 removed it from the conversation
  // sync payload). Other entity kinds (document/collection/canvas/space) include it.
  // When present it must be a number; absent is valid.
  if (x.version !== undefined && typeof x.version !== 'number') return false;
  if (!isString(x.createdAt)) return false;
  if (!isNullableString(x.createdBy)) return false;
  if (!isString(x.updatedAt)) return false;
  if (!isAccess(x.access)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// EntityMetaRow — narrows BaseEntityRow to kind === 'document'
// ---------------------------------------------------------------------------

export function isEntityMetaRow(x: unknown): x is EntityMetaRow {
  if (!isBaseEntityRow(x)) return false;
  return x.kind === 'document';
}

// ---------------------------------------------------------------------------
// CollectionRow — narrows BaseEntityRow to kind === 'collection'
// ---------------------------------------------------------------------------

export function isCollectionRow(x: unknown): x is CollectionRow {
  if (!isBaseEntityRow(x)) return false;
  return x.kind === 'collection';
}

// ---------------------------------------------------------------------------
// CanvasMetaRow — narrows BaseEntityRow to kind === 'canvas'
// ---------------------------------------------------------------------------

export function isCanvasMetaRow(x: unknown): x is CanvasMetaRow {
  if (!isBaseEntityRow(x)) return false;
  return x.kind === 'canvas';
}

// ---------------------------------------------------------------------------
// SpaceMetaRow — narrows BaseEntityRow + asserts kind-specific isDefault
// ---------------------------------------------------------------------------

export function isSpaceMetaRow(x: unknown): x is SpaceMetaRow {
  if (!isBaseEntityRow(x)) return false;
  if (x.kind !== 'space') return false;
  if (!isBoolean((x as unknown as { isDefault: unknown }).isDefault)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// ConversationRow — narrows BaseEntityRow to kind === 'conversation'
// ---------------------------------------------------------------------------

export function isConversationRow(x: unknown): x is ConversationRow {
  if (!isBaseEntityRow(x)) return false;
  if (x.kind !== 'conversation') return false;
  if (typeof (x as { isPinned?: unknown }).isPinned !== 'boolean') return false;
  if (typeof (x as { latestActivityAt?: unknown }).latestActivityAt !== 'string') return false;
  return true;
}

// ---------------------------------------------------------------------------
// SnapshotInfo
// ---------------------------------------------------------------------------

export function isSnapshotInfo(x: unknown): x is SnapshotInfo {
  if (!isObject(x)) return false;
  if (!isString(x.id)) return false;
  if (!isString(x.entityId)) return false;
  if (!isNullableString(x.name)) return false;
  if (!isString(x.timestamp)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Entity-event envelope
// ---------------------------------------------------------------------------

export interface ParsedEntityEvent {
  id: string;
  kind: string;
  modelKind: string;
  modelId: string;
  data: unknown;
}

/**
 * Validates the outer entity-event envelope (the `syncEvent` WS frame body).
 *
 * Returns a typed envelope on success, or null when the envelope is malformed.
 * Callers must drop and log malformed envelopes rather than applying them.
 *
 * The `data` field is returned as `unknown` so the caller can apply a
 * model-specific validator (isEntityMetaRow, isCollectionRow, isSnapshotInfo)
 * after checking `modelKind`.
 */
export function parseEntityEvent(raw: unknown): ParsedEntityEvent | null {
  if (!isObject(raw)) return null;
  if (!isString(raw.id)) return null;
  if (!isString(raw.kind)) return null;
  if (!isString(raw.modelKind)) return null;
  if (!isString(raw.modelId)) return null;
  return {
    id: raw.id,
    kind: raw.kind,
    modelKind: raw.modelKind,
    modelId: raw.modelId,
    data: raw.data ?? null,
  };
}
